import { NavLink, Navigate, Route, Routes, useLocation, useMatch, useNavigate, useNavigationType, useParams } from 'react-router-dom';
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { useAuth } from '@/lib/auth';
import { unreadCount } from '@/lib/channels';
import { seesChat, tabOf } from '@/lib/chatGroups';
import { PlusIcon } from '@/components/Icons';
import { syncPhoneAlerts } from '@/lib/phoneAlerts';
import { usePanels } from '@/lib/panels';
import { ChatsProvider, useChats } from '@/data/store';
import { OverlayProvider, SheetHost } from '@/components/Overlay';

import { LoginScreen } from '@/screens/LoginScreen';
import { SettingsMenu, SettingsScreen, useSettingsSection } from '@/screens/SettingsScreen';
import { ChatListScreen } from '@/screens/ChatListScreen';
import { ChatScreen } from '@/screens/ChatScreen';
import { AddTeamMembersScreen, CoachListScreen, NewTeamGroupScreen, TeamListScreen } from '@/screens/TeamScreens';
import { CommentThreadScreen, CommentsScreen } from '@/screens/CommentsScreen';
import { listOfComment, listOfReview, useComments } from '@/lib/comments';
import { TeamInfoScreen } from '@/screens/TeamInfoScreen';
import { DrcScreen } from '@/screens/DrcScreen';
import { TrainingScreen } from '@/screens/TrainingScreen';
import { DrcConversation, DrcHistory } from '@/screens/DrcDesk';
import { ArchivedScreen } from '@/screens/ArchivedScreen';
import { RemindersScreen } from '@/screens/RemindersScreen';
import { ContactScreen } from '@/screens/ContactScreen';
import { SetupScreen } from '@/screens/SetupScreen';
import { HelpScreen } from '@/screens/HelpScreen';
import { GuideLeoScreen, GuideScreen, GuideSetupScreen } from '@/screens/GuideScreen';
import { NewChatScreen } from '@/screens/NewChatScreen';
import { InPageAlert } from '@/components/InPageAlert';
import { EMBEDDED, isPhone, registerPush, reportOpen, useDevice, useInfoPanel, useWide, isIOS } from '@/lib/device';
import { noteScreen } from '@/lib/feedback';

// App shell: sign-in gate, the four tabs, and the chat screens (which hide the tabs, as WhatsApp does).
// Gary, 3 Nov 2026 (mock-up approved): Comments, Team, Coach, Chats, with Chats far right under the thumb; Ask is the
// round button above the bar; You is your picture top left of each main screen. Plan: docs/TODO.md.

interface TabDef {
  to: string;
  label: string;
  /** Drawn as an outline; the active tab fills its `chats-tab-shape` parts, as WhatsApp does. */
  icon: ReactNode;
}

// WhatsApp on iPhone, left to right: Updates, Calls, Communities, Chats, You. CMB Chats
// puts Dr C in Updates' place and Team in Communities'. There is no Calls tab: WhatsApp
// calls do not come through the API (Gary, 14 Oct).

const TABS: TabDef[] = [
  {
    to: '/comments',
    label: 'Comments',
    icon: (
      <svg className="chats-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true">
        <rect className="chats-tab-shape" x="3.5" y="4.5" width="17" height="12" rx="2.5" />
        <path d="M8 20l4-3.5" /><path d="M7.5 9h9M7.5 12h6" />
      </svg>
    ),
  },
  {
    to: '/team',
    label: 'Team',
    icon: (
      <svg className="chats-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
        <circle className="chats-tab-shape" cx="9" cy="8" r="3.4" />
        <path className="chats-tab-shape" d="M2.6 19.5c.8-3.4 3.3-5.3 6.4-5.3s5.6 1.9 6.4 5.3z" />
        <circle className="chats-tab-shape" cx="17.2" cy="9" r="2.5" />
        <path className="chats-tab-shape" d="M16.4 14.3c2.4-.2 4.3 1.5 5 4.6h-4.2" />
      </svg>
    ),
  },
  {
    to: '/coach',
    label: 'Coach',
    icon: (
      <svg className="chats-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
        <path className="chats-tab-shape" d="M4 20V10l8-6 8 6v10z" />
        <path d="M9 20v-5h6v5" />
      </svg>
    ),
  },
  {
    to: '/',
    label: 'Chats',
    icon: (
      <svg className="chats-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
        <path className="chats-tab-shape" d="M12 3.6c-4.9 0-8.9 3.4-8.9 7.7 0 2.3 1.2 4.3 3 5.7l-.6 3.5 3.6-1.9c.9.2 1.9.4 2.9.4 4.9 0 8.9-3.4 8.9-7.7S16.9 3.6 12 3.6z" />
      </svg>
    ),
  },
];

function Tabs() {
  const { access, session } = useAuth();
  const { chats, team } = useChats();
  const teamUnread = team.filter((t) => !t.archived && (t.unread ?? 0) > 0 && !t.coaching).length;
  const coachUnread = team.filter((t) => t.coaching && (t.unread ?? 0) > 0).length;
  // Comments waiting for someone, for the people who can answer them.
  const { items: comments, reviews } = useComments(access.comments === 'manage');
  // What a person must answer, as the Needs you list counts it (Gary, 3 Nov 2026).
  const commentsWaiting = comments.filter((c) => listOfComment(c) === 'Needs you').length + reviews.filter((r) => listOfReview(r) === 'Needs you').length;
  // Customer chats with something unread, as the tabs and rows count them (Gary, 20 Oct).
  const unread = unreadCount(chats.filter((c) => seesChat(c, access, session?.user.id)), Date.now());
  const location = useLocation();
  const navigate = useNavigate();
  const section = useSection(location.pathname);
  // Chats stays one step below the other tabs, so the phone's Back on Ask, Team or You returns to Chats and only
  // Back on Chats leaves the app, as WhatsApp on Android (mobile review, 19 Oct). Leaving Chats adds a step; moving
  // between the other tabs replaces it; the Chats tab steps back to that Chats rather than adding another.
  const overChats = (location.state as { overChats?: boolean } | null)?.overChats === true;
  const tabLink = (to: string) => to === '/'
    ? { replace: true, onClick: (e: MouseEvent) => { if (section !== '/' && overChats) { e.preventDefault(); navigate(-1); } } }
    : { replace: section !== '/', state: { overChats: section === '/' || overChats } };
  return (
    <nav className="chats-tabs" aria-label="Main">
      {TABS.map((tab) => (
        <NavLink key={tab.to} to={tab.to} end {...tabLink(tab.to)} className={() => `chats-tab${section === tab.to ? ' active' : ''}`}>
          {tab.icon}
          {tab.label}
          {tab.to === '/' && unread > 0 && <span className="chats-badge chats-badge--tab">{unread}</span>}
          {tab.to === '/team' && teamUnread > 0 && <span className="chats-badge chats-badge--tab">{teamUnread}</span>}
          {tab.to === '/coach' && coachUnread > 0 && <span className="chats-badge chats-badge--tab">{coachUnread}</span>}
          {tab.to === '/comments' && commentsWaiting > 0 && <span className="chats-badge chats-badge--tab">{commentsWaiting}</span>}
        </NavLink>
      ))}
      {/* New chat: the round "+" above the bar on the Chats list, where people look for it (Gary, 3 Nov 2026).
          Ask moved to the headers. */}
      {section === '/' && (
        <NavLink to="/new" className="chats-fab" aria-label="New chat" title="New chat"><PlusIcon /></NavLink>
      )}
    </nav>
  );
}

/** The tabs' own screens, where the tab bar shows. */
const TAB_ROOTS = ['/', '/drc', '/team', '/coach', '/comments', '/settings'];

/**
 * The tab a screen belongs to, knowing the chats: a coaching group opens at /team/:id but belongs to Coach, so
 * the Coach list stays on the left and Coach stays lit (Gary, 3 Nov 2026: Ballito jumped to the Team list).
 */
function useSection(pathname: string): string {
  const { team } = useChats();
  const section = tabSection(pathname);
  const id = pathname.match(/^\/team\/([^/]+)/)?.[1];
  if (section === '/team' && id && team.some((t) => t.id === id && t.coaching)) return '/coach';
  return section;
}

/** Which tab a screen belongs to. */
function tabSection(pathname: string): string {
  if (pathname === '/coach') return '/coach';
  if (pathname.startsWith('/comments')) return '/comments';
  if (pathname.startsWith('/team')) return '/team';
  if (pathname.startsWith('/drc')) return '/drc';
  if (pathname === '/settings' || pathname === '/help' || pathname === '/training') return '/settings';
  return '/';
}

/**
 * The browser tab shows how many chats are waiting, "(3) CMB Chats", as WhatsApp Web does,
 * so a computer user notices without a pop-up (Gary, 15 Oct). Customer chats and Team chats.
 */
function UnreadTitle() {
  const { access, session } = useAuth();
  const { chats, team, live } = useChats();
  const mine = chats.filter((c) => seesChat(c, access, session?.user.id));
  const now = Date.now();
  const n = unreadCount(mine, now) + team.filter((t) => !t.archived && (t.unread ?? 0) > 0).length;
  // What still needs you, for the phone's alerts and icon (Gary, 20 Oct): unread, marked unread or owed a reply.
  const needYou = [
    ...mine.filter((c) => !c.archived && (c.unread > 0 || c.markedUnread || tabOf(c, now) === 'reply')).map((c) => c.id),
    ...team.filter((t) => (t.unread ?? 0) > 0).map((t) => t.id),
  ].sort().join(',');
  useEffect(() => {
    document.title = n ? `(${n}) CMB Chats` : 'CMB Chats';
  }, [n]);
  useEffect(() => {
    if (!live) return;
    const sync = () => { if (document.visibilityState === 'visible') syncPhoneAlerts(n, new Set(needYou.split(',')), team.length > 0); };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, [live, n, needYou, team.length]);
  return null;
}

function NoAccess() {
  const { signOut, profileFailed, retryProfile } = useAuth();
  return (
    <div className="chats-app">
      <header className="chats-topbar">
        <h1 className="chats-topbar-title">CMB Chats</h1>
      </header>
      <main className="chats-screen">
        {profileFailed ? (
          // A failed load is not "no access" (Gary, 19 Oct): say so, and let them try again.
          <>
            <p className="chats-empty">Could not load your details. Check your signal, then try again.</p>
            <div className="chats-pad">
              <button className="chats-btn" type="button" onClick={retryProfile}>Try again</button>
            </div>
          </>
        ) : <p className="chats-empty">This app is for CleanMyBed operators, the POS team and head office. Your login does not have access yet. Ask head office if you think it should.</p>}
        <div className="chats-pad">
          <button className="chats-btn chats-btn--ghost" type="button" onClick={() => void signOut()}>Sign out</button>
        </div>
      </main>
    </div>
  );
}

/**
 * A fresh chat screen per chat, so its "unread messages" line and reply state never carry over.
 * Keyed on the chat and the message to open at (?m=) only: other markers in the address
 * (from=push) come and go and must not rebuild the chat and lose what is typed (16 Oct).
 */
function ChatRoute() {
  const { id } = useParams();
  const { search } = useLocation();
  return <ChatScreen key={`${id}${new URLSearchParams(search).get('m') ?? ''}`} />;
}

/**
 * A computer screen (Gary, 15 Oct): the list, the open chat and, when there is room, its
 * Info side by side in the full window. Narrower, Info takes the chat's place when opened.
 */
function Desk() {
  const { pathname, search } = useLocation();
  // A chat opened from Archived keeps Archived on the left until Back (Gary, 19 Oct: it jumped to the main list).
  // Leaving Archived by Back (the arrow or the browser's) brings the chat list back, whatever was open before
  // (Gary, 20 Oct: Back stepped to the earlier chat and left Archived showing, so it took two).
  const navType = useNavigationType();
  const listView = useRef<'main' | 'archived' | 'reminders'>('main');
  const lastPath = useRef(pathname);
  if (pathname === '/archived') listView.current = 'archived';
  else if (pathname === '/reminders') listView.current = 'reminders';
  else if (pathname === '/' || pathname === '/new') listView.current = 'main';
  else if ((lastPath.current === '/archived' || lastPath.current === '/reminders') && navType === 'POP') listView.current = 'main';
  lastPath.current = pathname;
  const room = useInfoPanel();
  const panels = usePanels();
  const chat = useMatch('/chat/:id/*');
  const team = useMatch('/team/:id/*');
  const section = useSection(pathname);
  const settingsSection = useSettingsSection();

  // Every tab the same shape (Gary, 15 Oct): its list on the left with the tabs under it,
  // what is open in the middle, Info on the right when there is room.
  let list: ReactNode;
  let main: ReactNode;
  let info: ReactNode = null;
  if (section === '/team') {
    const id = team?.params.id;
    const making = id === 'new-group' || id === 'new-channel';
    list = <TeamListScreen />;
    if (making) main = <NewTeamGroupScreen key={id} channel={id === 'new-channel'} />;
    else if (id) {
      const infoOpen = pathname.endsWith('/info');
      if (pathname.endsWith('/add')) main = <AddTeamMembersScreen />;
      else main = infoOpen && !room ? <TeamInfoScreen /> : <ChatScreen key={`team-${id}${new URLSearchParams(search).get('m') ?? ''}`} team />;
      if (room) info = <TeamInfoScreen />;
    } else main = <DeskEmpty text="Message anyone on the team, or a group. Customers never see these chats." />;
  } else if (section === '/coach') {
    list = <CoachListScreen />;
    const id = team?.params.id;
    if (id) {
      main = pathname.endsWith('/info') && !room ? <TeamInfoScreen /> : <ChatScreen key={`team-${id}${new URLSearchParams(search).get('m') ?? ''}`} team />;
      if (room) info = <TeamInfoScreen />;
    } else main = <DeskEmpty text="Ted coaches each area in its own group: the week's sales plan and tasks." />;
  } else if (section === '/comments') {
    list = <CommentsScreen />;
    const open = pathname.match(/^\/comments\/(c|r)\//)?.[1] as 'c' | 'r' | undefined;
    main = open ? <CommentThreadScreen key={pathname} kind={open} />
      : <DeskEmpty text="Comments on our Facebook, Instagram and ads, and our Google and Facebook reviews, with Leo's suggested replies. Pick one on the left." />;
  } else if (section === '/drc') {
    list = <DrcHistory />;
    main = <DrcConversation />;
  } else if (section === '/settings') {
    list = <SettingsMenu />;
    main = pathname === '/help' ? <HelpScreen /> : pathname === '/training' ? <TrainingScreen /> : <SettingsScreen section={settingsSection} />;
  } else {
    const id = chat?.params.id;
    list = listView.current === 'archived' ? <ArchivedScreen /> : listView.current === 'reminders' ? <RemindersScreen /> : <ChatListScreen />;
    if (pathname === '/new') main = <NewChatScreen />;
    else if (id) {
      main = pathname.endsWith('/info') && !room ? <ContactScreen /> : <ChatScreen key={`${id}${new URLSearchParams(search).get('m') ?? ''}`} />;
      if (room) info = <ContactScreen />;
    } else main = <DeskEmpty text="WhatsApp for CleanMyBed customers. Pick a chat on the left, or start a new one."
      action={<NavLink to="/new" className="chats-btn chats-desk-welcome-btn">New chat</NavLink>} />;
  }
  // A chat open in the middle can have either side hidden for full width (Gary, 18 Oct).
  const chatOpen = !!(chat?.params.id || team?.params.id);
  const hideList = chatOpen && !panels.list;
  return (
    <div className={`chats-desk${hideList ? ' chats-desk--nolist' : ''}`}>
      <aside className="chats-desk-list">
        {list}
        <Tabs />
      </aside>
      <section className={`chats-desk-chat${section === '/settings' ? ' chats-desk-chat--page' : ''}`}>{main}</section>
      {info && panels.info && <aside className="chats-desk-info">{info}</aside>}
    </div>
  );
}

/** The middle of a computer screen before anything is open: the app's mark and what the tab is for. */
function DeskEmpty({ text, action }: { text: string; action?: ReactNode }) {
  return (
    <div className="chats-desk-welcome">
      <img src={`${import.meta.env.BASE_URL}icon-192.png`} alt="" className="chats-desk-welcome-logo" />
      <h2>CMB Chats</h2>
      <p>{text}</p>
      {action}
    </div>
  );
}

/** A Team chat opens in the same chat screen as a customer chat (Gary, 15 Oct). */
function TeamChatRoute() {
  const { id } = useParams();
  const { search } = useLocation();
  return <ChatScreen key={`team-${id}${new URLSearchParams(search).get('m') ?? ''}`} team />;
}

/**
 * A phone sees set-up until a test alert has been tapped on it (Gary, 19 Oct). iPhone also needs the home screen;
 * Android does not (alerts work in Chrome). Inside Sentinel it never shows. "Not now" always lets them through.
 */
function needsSetup(standalone: boolean, notify: string): boolean {
  const forced = import.meta.env.DEV && new URLSearchParams(window.location.search).has('setup');
  const preview = import.meta.env.DEV && sessionStorage.getItem('chats.preview') !== null;
  if (forced) return true;
  if (preview || !isPhone || EMBEDDED) return false;
  // The test alert is optional (Gary, 19 Oct): it never keeps anyone out of the app.
  return (isIOS && !standalone) || notify !== 'granted';
}

function SignedIn() {
  const { session } = useAuth();
  const { standalone, notify } = useDevice();
  const userId = session?.user.id;
  // Once notifications are allowed, register this phone for messages with the app closed.
  // Coming back to the front counts as opening the app (readiness board), at most every 5 minutes.
  useEffect(() => {
    if (notify !== 'granted' || !userId || userId === 'preview') return;
    let last = Date.now();
    void registerPush(userId);
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 5 * 60_000) return;
      last = Date.now();
      void registerPush(userId);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [notify, userId]);
  // Every open, alerts or not, for head office's board (22 Oct 2026): on start and on coming back, at most every 5 minutes.
  useEffect(() => {
    if (!userId || userId === 'preview') return;
    let last = Date.now();
    void reportOpen();
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 5 * 60_000) return;
      last = Date.now();
      void reportOpen();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [userId]);
  // A tapped notification opens its chat inside the running app (sw.ts), instead of
  // reloading the whole app (Gary, 16 Oct). The opened alert still counts for the readiness board.
  const navigate = useNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: string; url?: string } | null;
      if (d?.type !== 'open' || !d.url) return;
      e.ports[0]?.postMessage('opened');
      // Already on that chat: stay, without a second copy of it in history.
      const same = new URL(d.url, window.location.origin).pathname === window.location.pathname;
      navigate(d.url, { replace: same });
      // registerPush reads the real permission itself (review, 19 Oct: a copy said "not asked" and the tap was lost).
      if (userId && userId !== 'preview') void registerPush(userId);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate, notify, userId]);
  const [skipped, setSkipped] = useState(() => sessionStorage.getItem('chats.setupSkipped') === '1');
  const [finished, setFinished] = useState(false);
  const { pathname, search } = useLocation();
  // The bug icon sends the screen before the one it was tapped on (Gary, 19 Oct).
  useEffect(() => { noteScreen(pathname + search); }, [pathname, search]);
  const navType = useNavigationType();
  // The screen the app opened on does not slide in.
  const opened = useRef(false);
  useEffect(() => { opened.current = true; }, []);
  // Worked out once per screen: a sheet closing steps history back on the same screen, and
  // that must not replay the slide.
  const slide = useRef({ path: pathname, cls: '' });
  if (slide.current.path !== pathname) {
    // Tab to tab just appears, now that leaving Chats adds a history step (mobile review, 19 Oct).
    const tabSwitch = TAB_ROOTS.includes(slide.current.path) && TAB_ROOTS.includes(pathname);
    slide.current = { path: pathname, cls: tabSwitch ? '' : navType === 'PUSH' ? ' chats-route--in' : navType === 'POP' && opened.current ? ' chats-route--back' : '' };
  }
  // Wide enough for the list and the chat side by side: computers and tablets, held either
  // way (Gary, 16 Oct). No phone is this wide, even turned sideways.
  const wide = useWide();
  if (!finished && !skipped && needsSetup(standalone, notify)) {
    return (
      <SetupScreen
        onDone={() => {
          if (notify === 'denied') {
            sessionStorage.setItem('chats.setupSkipped', '1');
            setSkipped(true);
          }
          setFinished(true);
        }}
      />
    );
  }
  // An open Ask chat on a phone is full screen too, as a chat (mobile review, 18 Oct).
  const askOpen = pathname === '/drc' && /[?&](c|new)=/.test(search);
  const inChat = askOpen || pathname.startsWith('/chat/') || pathname.startsWith('/comments/') || pathname === '/archived' || pathname === '/reminders' || pathname === '/help' || pathname === '/training' || pathname === '/new' || /^\/team\/.+/.test(pathname);
  if (wide) {
    return (
      <div className="chats-app chats-app--desk">
        <UnreadTitle />
        <InPageAlert />
        <Routes>
          {/* The same paths as on a phone, so each screen reads its own id. */}
          {['/', '/archived', '/reminders', '/new', '/chat/:id', '/chat/:id/info', '/team', '/team/:id', '/team/:id/info', '/team/:id/add', '/coach', '/comments', '/comments/c/:id', '/comments/r/:id', '/drc', '/settings', '/help', '/training'].map((p) => (
            <Route key={p} path={p} element={<Desk />} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    );
  }
  return (
    <div className="chats-app">
      <UnreadTitle />
      <InPageAlert />
      {/* Screens slide in as on a phone (Gary, 16 Oct): a new screen from the right, going back
          from the left. Tab switches and replaced screens just appear, as WhatsApp. */}
      <div key={pathname} className={`chats-route${slide.current.cls}`}>
      <Routes>
        <Route path="/" element={<ChatListScreen />} />
        <Route path="/chat/:id" element={<ChatRoute />} />
        <Route path="/chat/:id/info" element={<ContactScreen />} />
        <Route path="/archived" element={<ArchivedScreen />} />
        <Route path="/reminders" element={<RemindersScreen />} />
        <Route path="/team" element={<TeamListScreen />} />
        <Route path="/coach" element={<CoachListScreen />} />
        <Route path="/comments" element={<CommentsScreen />} />
        <Route path="/comments/c/:id" element={<CommentThreadScreen kind="c" />} />
        <Route path="/comments/r/:id" element={<CommentThreadScreen kind="r" />} />
        <Route path="/team/new-group" element={<NewTeamGroupScreen />} />
        <Route path="/team/new-channel" element={<NewTeamGroupScreen channel />} />
        <Route path="/team/:id" element={<TeamChatRoute />} />
        <Route path="/team/:id/info" element={<TeamInfoScreen />} />
        <Route path="/team/:id/add" element={<AddTeamMembersScreen />} />
        <Route path="/drc" element={<DrcScreen />} />
        <Route path="/training" element={<TrainingScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/help" element={<HelpScreen />} />
        <Route path="/new" element={<NewChatScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </div>
      {!inChat && <Tabs />}
    </div>
  );
}

export function App() {
  const { session, access, loading } = useAuth();
  const { pathname } = useLocation();

  // The step-by-step guide opens for anyone, signed in or not, so it can be sent as a link.
  if (pathname === '/guide') return <GuideScreen />;
  if (pathname === '/guide/setup') return <GuideSetupScreen />;
  if (pathname === '/guide/leo') return <GuideLeoScreen />;
  if (loading) {
    return (
      <div className="boot">
        <div className="boot-spin" />
      </div>
    );
  }
  if (!session) return <LoginScreen />;
  if (!access.allowed) return <NoAccess />;

  return (
    <OverlayProvider>
      <ChatsProvider>
        <SignedIn />
        <SheetHost />
      </ChatsProvider>
    </OverlayProvider>
  );
}
